import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Button, Dialog,
  DialogContent, TextField, Grid, Chip, Alert,
  Autocomplete, TableSortLabel, LinearProgress,
  Card, CardContent, useTheme, useMediaQuery,
  ToggleButton, ToggleButtonGroup, MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  Image as ImageIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import SwipeableCard from './SwipeableCard';
import api from '../api';
import { useAuth } from '../AuthContext';
import { useColumnSettings } from '../hooks/useColumnSettings';
import { navyBlue } from '../constants/colors';
import { useThemeColors } from '../hooks/useThemeColors';

export default function WorkOrders() {
  const [workOrders, setWorkOrders] = useState([]);
  const [assets, setAssets] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [janitors, setJanitors] = useState([]);
  const [itTechnicians, setItTechnicians] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({});
  const [error, setError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]); // [{file, preview}]
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [orderByOpen, setOrderByOpen] = useState('work_order_number');
  const [orderOpen, setOrderOpen] = useState('desc');
  const [orderByCompleted, setOrderByCompleted] = useState('completed_at');
  const [orderCompleted, setOrderCompleted] = useState('desc');
  const [completedDateRange, setCompletedDateRange] = useState('7');
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t, i18n } = useTranslation();
  const {
    priorityColors,
    statusColors,
    overdueColors,
    issueTypeColors,
    tableRowAlt,
    tableRowHover,
    borderColor,
    pmTypeBackground,
    pmTypeText,
    defaultTypeBackground,
    defaultTypeText,
    hoverBackground,
    accentBlue,
    gradientPrimary,
    gradientPrimaryHover,
  } = useThemeColors();

  const getLocalizedText = (obj, field) => {
    const isSpanish = i18n.language === 'es';
    const spanishField = `${field}_es`;
    if (isSpanish && obj[spanishField]) {
      return obj[spanishField];
    }
    return obj[field];
  };

  const defaultColumns = ['work_order_number', 'asset_number', 'asset_description', 'type', 'issue_type', 'title', 'priority', 'status', 'assigned_to_name', 'due_date', 'created_at', 'completed_at'];
  const { visibleColumns } = useColumnSettings('work_orders', defaultColumns);

  const columnDefinitions = [
    { id: 'work_order_number', label: t('workOrders.workOrderNumber'), sortKey: 'work_order_number' },
    { id: 'asset_number', label: t('workOrders.assetNumber'), sortKey: 'asset_number' },
    { id: 'asset_description', label: t('workOrders.assetName'), sortKey: 'asset_description' },
    { id: 'type', label: t('workOrders.type'), sortKey: 'type' },
    { id: 'issue_type', label: t('workOrders.issueType') || 'Issue Type', sortKey: 'assignment_type' },
    { id: 'title', label: t('workOrders.workOrderTitle'), sortKey: 'title' },
    { id: 'priority', label: t('workOrders.priority'), sortKey: 'priority' },
    { id: 'status', label: t('workOrders.status'), sortKey: 'status' },
    { id: 'assigned_to_name', label: t('workOrders.assignedTo'), sortKey: 'assigned_to_name' },
    { id: 'due_date', label: t('workOrders.dueDate') || 'Due Date', sortKey: 'due_date' },
    { id: 'created_at', label: t('workOrders.created'), sortKey: 'created_at' },
    { id: 'completed_at', label: t('workOrders.completedAt'), sortKey: 'completed_at' },
  ];

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [woRes, assetsRes, techRes, janitorRes, itRes] = await Promise.all([
        api.get('/work-orders'),
        api.get('/assets'),
        api.get('/users/technicians'),
        api.get('/users/janitors'),
        api.get('/users/it-technicians'),
      ]);
      setWorkOrders(woRes.data);
      setAssets(assetsRes.data);
      setTechnicians(techRes.data);
      setJanitors(janitorRes.data);
      setItTechnicians(itRes.data);
    } catch (err) {
      setError(t('workOrders.failedToLoad'));
    }
  };

  const selectedAsset = assets.find(a => a.id === formData.asset_id);
  const isFacilitiesAsset = selectedAsset?.category === 'Facilities';
  const isEquipmentAsset = selectedAsset?.category === 'Equipment';
  const showAssignmentOptions = isFacilitiesAsset || isEquipmentAsset;

  const getAssignableUsers = () => {
    if (formData.assignment_type === 'janitor') {
      return janitors;
    }
    if (formData.assignment_type === 'it_technician') {
      return itTechnicians;
    }
    return technicians;
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('workOrders.deleteConfirm'))) {
      return;
    }
    try {
      await api.delete(`/work-orders/${id}`);
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || t('workOrders.failedToDelete'));
    }
  };

  // Swipe action handlers for mobile
  const handleSwipeComplete = async (woId) => {
    if (!window.confirm(t('workOrders.confirmComplete') || 'Mark this work order as complete?')) return;
    try {
      await api.patch(`/work-orders/${woId}/status`, { status: 'completed' });
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to complete work order');
    }
  };

  const handleSwipePriority = (woId) => {
    navigate(`/work-orders/${woId}`);
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const newItems = files.map(file => ({
      file,
      preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    }));
    setSelectedFiles(prev => [...prev, ...newItems]);
    e.target.value = '';
  };

  const handleRemoveFile = (idx) => {
    setSelectedFiles(prev => {
      const item = prev[idx];
      if (item?.preview) URL.revokeObjectURL(item.preview);
      return prev.filter((_, i) => i !== idx);
    });
  };

  const clearAllFiles = () => {
    selectedFiles.forEach(a => { if (a.preview) URL.revokeObjectURL(a.preview); });
    setSelectedFiles([]);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({});
    setError('');
    clearAllFiles();
  };

  const handleSubmit = async () => {
    if (!formData.asset_id || !formData.title) {
      setError(t('workOrders.assetAndTitleRequired'));
      return;
    }
    setSubmitting(true);
    setUploadProgress(0);

    try {
      let workOrderId = formData.id;

      if (formData.id) {
        await api.put(`/work-orders/${formData.id}`, formData);
      } else {
        const response = await api.post('/work-orders', formData);
        workOrderId = response.data.id;
      }

      // Upload attachments (only for new work orders)
      if (selectedFiles.length > 0 && workOrderId && !formData.id) {
        for (let i = 0; i < selectedFiles.length; i++) {
          const photoData = new FormData();
          photoData.append('file', selectedFiles[i].file);
          await api.post(`/uploads/work-order/${workOrderId}`, photoData, {
            onUploadProgress: (progressEvent) => {
              const fileProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              const overall = Math.round(((i + fileProgress / 100) / selectedFiles.length) * 100);
              setUploadProgress(overall);
            }
          });
        }
      }

      handleCloseDialog();
      loadData();
    } catch (err) {
      setError(err.response?.data?.error || (formData.id ? t('workOrders.failedToUpdate') : t('workOrders.failedToCreate')));
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  // Toggle button styles
  const toggleButtonStyle = {
    borderRadius: '20px !important',
    border: `1.5px solid ${navyBlue} !important`,
    py: 0.5,
    px: 2,
    fontSize: '0.85rem',
    textTransform: 'none',
    '&.Mui-selected': {
      backgroundColor: navyBlue,
      color: 'white',
      '&:hover': {
        backgroundColor: '#2c4a6e',
      },
    },
    '&:hover': {
      backgroundColor: hoverBackground,
    },
  };

  // Custom styled chip for priority
  const PriorityChip = ({ priority }) => {
    const colors = priorityColors[priority] || priorityColors.medium;
    const label = priority === 'low' ? t('workOrders.priorityLow') : priority === 'medium' ? t('workOrders.priorityMedium') : priority === 'high' ? t('workOrders.priorityHigh') : t('workOrders.priorityEmergency');
    return (
      <Chip
        label={label}
        size="small"
        sx={{
          backgroundColor: colors.bg,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          fontWeight: 600,
        }}
      />
    );
  };

  // Custom styled chip for status
  const StatusChip = ({ status }) => {
    const colors = statusColors[status] || statusColors.assigned;
    const label = status === 'assigned' ? t('workOrders.statusAssigned') : status === 'in_progress' ? t('workOrders.statusInProgress') : t('workOrders.statusCompleted');
    return (
      <Chip
        label={label}
        size="small"
        sx={{
          backgroundColor: colors.bg,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          fontWeight: 600,
        }}
      />
    );
  };

  // Custom styled chip for issue type (assignment_type)
  const IssueTypeChip = ({ assignmentType }) => {
    const colors = issueTypeColors[assignmentType] || issueTypeColors.technician;
    let label;
    switch (assignmentType) {
      case 'janitor':
        label = t('workOrders.janitorialRequest') || 'Janitorial';
        break;
      case 'it_technician':
        label = t('workOrders.itIssue') || 'IT/Computer';
        break;
      default:
        label = t('workOrders.maintenanceIssue') || 'Maintenance';
    }
    return (
      <Chip
        label={label}
        size="small"
        sx={{
          backgroundColor: colors.bg,
          color: colors.text,
          border: `1px solid ${colors.border}`,
          fontWeight: 600,
        }}
      />
    );
  };

  const renderCell = (wo, columnId) => {
    switch (columnId) {
      case 'work_order_number':
        return (
          <Typography
            variant="body2"
            sx={{
              color: accentBlue,
              cursor: 'pointer',
              fontWeight: 'bold',
              '&:hover': { textDecoration: 'underline' }
            }}
            onClick={() => navigate(`/work-orders/${wo.id}`)}
          >
            {wo.work_order_number}
          </Typography>
        );
      case 'asset_number':
        return wo.asset_number;
      case 'asset_description':
        return wo.asset_description;
      case 'type':
        return (
          <Chip
            label={wo.type === 'preventive_maintenance' ? t('workOrders.typePM') : t('workOrders.typeRequest')}
            size="small"
            sx={{
              backgroundColor: wo.type === 'preventive_maintenance' ? pmTypeBackground : defaultTypeBackground,
              color: wo.type === 'preventive_maintenance' ? pmTypeText : defaultTypeText,
              fontWeight: 500,
            }}
          />
        );
      case 'issue_type':
        return <IssueTypeChip assignmentType={wo.assignment_type} />;
      case 'title':
        return getLocalizedText(wo, 'title');
      case 'priority':
        return <PriorityChip priority={wo.priority} />;
      case 'status':
        return <StatusChip status={wo.status} />;
      case 'assigned_to_name':
        return wo.assigned_to_name || t('workOrders.unassigned');
      case 'due_date':
        if (!wo.due_date) return '-';
        const isOverdue = new Date(wo.due_date) < new Date() && wo.status !== 'completed';
        return (
          <Typography
            variant="body2"
            sx={{
              color: isOverdue ? overdueColors.text : 'inherit',
              fontWeight: isOverdue ? 600 : 400,
            }}
          >
            {new Date(wo.due_date).toLocaleDateString()}
          </Typography>
        );
      case 'created_at':
        return wo.created_at ? new Date(wo.created_at).toLocaleDateString() : '-';
      case 'completed_at':
        return wo.completed_at ? new Date(wo.completed_at).toLocaleString() : 'N/A';
      default:
        return null;
    }
  };

  const handleRequestSortOpen = (property) => {
    const isAsc = orderByOpen === property && orderOpen === 'asc';
    setOrderOpen(isAsc ? 'desc' : 'asc');
    setOrderByOpen(property);
  };

  const handleRequestSortCompleted = (property) => {
    const isAsc = orderByCompleted === property && orderCompleted === 'asc';
    setOrderCompleted(isAsc ? 'desc' : 'asc');
    setOrderByCompleted(property);
  };

  const sortData = (data, orderBy, order) => {
    return [...data].sort((a, b) => {
      let aVal = a[orderBy];
      let bVal = b[orderBy];
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (order === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
  };

  // Mobile card component — compact layout
  const WorkOrderCard = ({ wo }) => (
    <Card
      sx={{
        mb: 1.5,
        cursor: 'pointer',
        borderRadius: 2,
        border: `1px solid ${borderColor}`,
        '&:active': {
          transform: 'scale(0.98)',
          transition: 'transform 0.1s ease-in-out',
        }
      }}
      onClick={() => navigate(`/work-orders/${wo.id}`)}
    >
      <CardContent sx={{ p: 1.5, pb: '10px !important' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 'bold', color: accentBlue }}>
            {wo.work_order_number}
          </Typography>
          <StatusChip status={wo.status} />
        </Box>

        <Typography variant="body2" sx={{ fontSize: '0.8rem', mb: 0.25 }} color="text.secondary">
          {wo.asset_number} - {wo.asset_description}
        </Typography>

        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 500, mb: 0.5 }} color="text.primary">
          {getLocalizedText(wo, 'title')}
        </Typography>

        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
          <Chip
            label={wo.type === 'preventive_maintenance' ? t('workOrders.typePM') : wo.type}
            size="small"
            sx={{
              backgroundColor: wo.type === 'preventive_maintenance' ? pmTypeBackground : defaultTypeBackground,
              color: wo.type === 'preventive_maintenance' ? pmTypeText : defaultTypeText,
              fontWeight: 500,
              fontSize: '0.65rem',
              height: 22,
            }}
          />
          <IssueTypeChip assignmentType={wo.assignment_type} />
          <PriorityChip priority={wo.priority} />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            {wo.assigned_to_name || t('workOrders.unassigned')} · {wo.created_at ? new Date(wo.created_at).toLocaleDateString() : '-'}
          </Typography>

          {wo.due_date && wo.status !== 'completed' && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.7rem',
                color: new Date(wo.due_date) < new Date() ? overdueColors.text : 'text.secondary',
                fontWeight: new Date(wo.due_date) < new Date() ? 600 : 400,
              }}
            >
              {new Date(wo.due_date) < new Date() ? 'OVERDUE' : `Due ${new Date(wo.due_date).toLocaleDateString()}`}
            </Typography>
          )}

          {wo.completed_at && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              {new Date(wo.completed_at).toLocaleDateString()}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const openWorkOrders = sortData(
    workOrders.filter((wo) => wo.status !== 'completed'),
    orderByOpen,
    orderOpen
  );

  const completedWorkOrders = sortData(
    workOrders.filter((wo) => {
      if (wo.status !== 'completed') return false;
      if (completedDateRange === 'all') return true;
      const daysAgo = parseInt(completedDateRange);
      const completedDate = new Date(wo.completed_at || wo.updated_at);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysAgo);
      return completedDate >= cutoffDate;
    }),
    orderByCompleted,
    orderCompleted
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" sx={{ color: accentBlue, fontWeight: 600 }}>{t('workOrders.title')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          sx={{
            background: gradientPrimary,
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            '&:hover': {
              background: gradientPrimaryHover,
            },
          }}
        >
          {t('workOrders.newRequest')}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Open Work Orders Section */}
      <Typography variant="h5" sx={{ mb: 2, color: accentBlue, fontWeight: 600 }}>{t('workOrders.openWorkOrders')}</Typography>
      {isMobile ? (
        <Box sx={{ mb: 4 }}>
          {openWorkOrders.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="body2" color="textSecondary">{t('workOrders.noOpenWorkOrders')}</Typography>
            </Paper>
          ) : (
            openWorkOrders.map((wo) => (
              <SwipeableCard
                key={wo.id}
                onComplete={() => handleSwipeComplete(wo.id)}
                onPriority={() => handleSwipePriority(wo.id)}
                disabled={wo.status === 'completed'}
              >
                <WorkOrderCard wo={wo} />
              </SwipeableCard>
            ))
          )}
        </Box>
      ) : (
        <Paper sx={{ mb: 4, borderRadius: 2, overflow: 'hidden', border: `1px solid ${borderColor}` }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: navyBlue }}>
                  {visibleColumns.map((columnId) => {
                    const column = columnDefinitions.find(col => col.id === columnId);
                    if (!column) return null;
                    return (
                      <TableCell key={columnId} sx={{ color: 'white', fontWeight: 600 }}>
                        <TableSortLabel
                          active={orderByOpen === column.sortKey}
                          direction={orderByOpen === column.sortKey ? orderOpen : 'asc'}
                          onClick={() => handleRequestSortOpen(column.sortKey)}
                          sx={{
                            color: 'white !important',
                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          }}
                        >
                          {column.label}
                        </TableSortLabel>
                      </TableCell>
                    );
                  })}
                  {hasRole('administrator', 'supervisor') && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('workOrders.actions')}</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {openWorkOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + (hasRole('administrator', 'supervisor') ? 1 : 0)} align="center">
                      <Typography variant="body2" color="textSecondary">{t('workOrders.noOpenWorkOrders')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  openWorkOrders.map((wo, index) => (
                    <TableRow
                      key={wo.id}
                      hover
                      onClick={() => navigate(`/work-orders/${wo.id}`)}
                      sx={{
                        backgroundColor: index % 2 === 0 ? tableRowAlt : 'inherit',
                        '&:hover': { backgroundColor: tableRowHover },
                        cursor: 'pointer',
                      }}
                    >
                      {visibleColumns.map((columnId) => (
                        <TableCell key={columnId}>{renderCell(wo, columnId)}</TableCell>
                      ))}
                      {hasRole('administrator', 'supervisor') && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <IconButton size="small" color="error" onClick={() => handleDelete(wo.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Completed Work Orders Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 4, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" sx={{ color: accentBlue, fontWeight: 600 }}>{t('workOrders.completedWorkOrders')}</Typography>
        <ToggleButtonGroup
          value={completedDateRange}
          exclusive
          onChange={(e, val) => val && setCompletedDateRange(val)}
          size="small"
          sx={{ flexWrap: 'wrap' }}
        >
          <ToggleButton value="7" sx={toggleButtonStyle}>{t('workOrders.last7Days')}</ToggleButton>
          <ToggleButton value="30" sx={toggleButtonStyle}>{t('workOrders.last30Days')}</ToggleButton>
          <ToggleButton value="90" sx={toggleButtonStyle}>{t('workOrders.last90Days')}</ToggleButton>
          <ToggleButton value="all" sx={toggleButtonStyle}>{t('workOrders.allTime')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {isMobile ? (
        <Box>
          {completedWorkOrders.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="body2" color="textSecondary">{t('workOrders.noCompletedWorkOrders')}</Typography>
            </Paper>
          ) : (
            completedWorkOrders.map((wo) => <WorkOrderCard key={wo.id} wo={wo} />)
          )}
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${borderColor}` }}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: navyBlue }}>
                  {visibleColumns.map((columnId) => {
                    const column = columnDefinitions.find(col => col.id === columnId);
                    if (!column) return null;
                    return (
                      <TableCell key={columnId} sx={{ color: 'white', fontWeight: 600 }}>
                        <TableSortLabel
                          active={orderByCompleted === column.sortKey}
                          direction={orderByCompleted === column.sortKey ? orderCompleted : 'asc'}
                          onClick={() => handleRequestSortCompleted(column.sortKey)}
                          sx={{
                            color: 'white !important',
                            '& .MuiTableSortLabel-icon': { color: 'white !important' },
                          }}
                        >
                          {column.label}
                        </TableSortLabel>
                      </TableCell>
                    );
                  })}
                  {hasRole('administrator', 'supervisor') && (
                    <TableCell sx={{ color: 'white', fontWeight: 600 }}>{t('workOrders.actions')}</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {completedWorkOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={visibleColumns.length + (hasRole('administrator', 'supervisor') ? 1 : 0)} align="center">
                      <Typography variant="body2" color="textSecondary">{t('workOrders.noCompletedWorkOrders')}</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  completedWorkOrders.map((wo, index) => (
                    <TableRow
                      key={wo.id}
                      hover
                      onClick={() => navigate(`/work-orders/${wo.id}`)}
                      sx={{
                        backgroundColor: index % 2 === 0 ? tableRowAlt : 'inherit',
                        '&:hover': { backgroundColor: tableRowHover },
                        cursor: 'pointer',
                      }}
                    >
                      {visibleColumns.map((columnId) => (
                        <TableCell key={columnId}>{renderCell(wo, columnId)}</TableCell>
                      ))}
                      {hasRole('administrator', 'supervisor') && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <IconButton size="small" color="error" onClick={() => handleDelete(wo.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Work Order Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <Box sx={{ background: gradientPrimary, py: 2, px: 3 }}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
            {formData.id ? t('workOrders.editWorkOrder') : t('workOrders.createMaintenanceRequest')}
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Autocomplete
                options={assets}
                getOptionLabel={(asset) => `${asset.asset_number} - ${asset.asset_description}`}
                value={assets.find(a => a.id === formData.asset_id) || null}
                onChange={(event, newValue) => {
                  const newAsset = newValue;
                  const isFacilities = newAsset?.category === 'Facilities';
                  const isEquipment = newAsset?.category === 'Equipment';
                  const hasOptions = isFacilities || isEquipment;
                  setFormData({
                    ...formData,
                    asset_id: newAsset ? newAsset.id : '',
                    assignment_type: hasOptions ? (formData.assignment_type || 'technician') : 'technician',
                    assigned_to: hasOptions ? formData.assigned_to : ''
                  });
                  // Blur the input to dismiss keyboard on mobile after selection
                  if (newValue && document.activeElement) {
                    document.activeElement.blur();
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label={t('workOrders.assetRequired')} required fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
                )}
              />
            </Grid>
            {showAssignmentOptions && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
                  {t('workOrders.issueType') || 'Issue Type'}
                </Typography>
                <ToggleButtonGroup
                  value={formData.assignment_type || 'technician'}
                  exclusive
                  onChange={(e, val) => val && setFormData({ ...formData, assignment_type: val, assigned_to: '' })}
                  sx={{ gap: 1 }}
                >
                  <ToggleButton value="technician" sx={toggleButtonStyle}>
                    {t('workOrders.maintenanceIssue') || 'Maintenance Issue'}
                  </ToggleButton>
                  {isFacilitiesAsset && (
                    <ToggleButton value="janitor" sx={{
                      ...toggleButtonStyle,
                      border: '1.5px solid #4caf50 !important',
                      '&.Mui-selected': { backgroundColor: '#4caf50', color: 'white', '&:hover': { backgroundColor: '#66bb6a' } },
                      '&:hover': { backgroundColor: '#e8f5e9' }
                    }}>
                      {t('workOrders.janitorialRequest') || 'Janitorial Request'}
                    </ToggleButton>
                  )}
                  {isEquipmentAsset && (
                    <ToggleButton value="it_technician" sx={{
                      ...toggleButtonStyle,
                      border: '1.5px solid #9c27b0 !important',
                      '&.Mui-selected': { backgroundColor: '#9c27b0', color: 'white', '&:hover': { backgroundColor: '#ab47bc' } },
                      '&:hover': { backgroundColor: '#f3e5f5' }
                    }}>
                      {t('workOrders.itIssue') || 'IT/Computer Issue'}
                    </ToggleButton>
                  )}
                </ToggleButtonGroup>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('workOrders.titleRequired')}
                required
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('workOrders.description')}
                multiline
                rows={4}
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Grid>

            {/* Attachments - only for new work orders */}
            {!formData.id && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
                  {i18n.language === 'es' ? 'Adjuntar archivos (opcional)' : 'Attachments (optional)'}
                </Typography>

                <Paper
                  component="label"
                  elevation={0}
                  sx={{
                    border: `2px dashed ${borderColor}`,
                    borderRadius: 2,
                    p: 2.5,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                    bgcolor: 'transparent',
                    '&:hover': {
                      borderColor: accentBlue,
                      bgcolor: tableRowAlt
                    }
                  }}
                >
                  <input
                    type="file"
                    accept="image/*,application/pdf"
                    multiple
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  <AttachFileIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    {i18n.language === 'es' ? 'Toca para adjuntar fotos o PDFs' : 'Tap to attach photos or PDFs'}
                  </Typography>
                </Paper>

                {selectedFiles.length > 0 && (
                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {selectedFiles.map((item, idx) => (
                      <Paper key={idx} sx={{ p: 1.5, borderRadius: 2, bgcolor: tableRowAlt }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {item.preview ? (
                            <Box component="img" src={item.preview} alt="Preview" sx={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 1, flexShrink: 0 }} />
                          ) : (
                            <Box sx={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: borderColor, borderRadius: 1, flexShrink: 0 }}>
                              {item.file.type === 'application/pdf'
                                ? <PdfIcon sx={{ fontSize: 24, color: '#ef5350' }} />
                                : <ImageIcon sx={{ fontSize: 24, color: 'text.secondary' }} />}
                            </Box>
                          )}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight="medium" noWrap>{item.file.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{(item.file.size / 1024 / 1024).toFixed(2)} MB</Typography>
                          </Box>
                          <IconButton onClick={() => handleRemoveFile(idx)} size="small" sx={{ color: '#f44336', flexShrink: 0 }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Grid>
            )}

            {/* Upload Progress */}
            {submitting && uploadProgress > 0 && uploadProgress < 100 && (
              <Grid item xs={12}>
                <LinearProgress
                  variant="determinate"
                  value={uploadProgress}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    bgcolor: borderColor,
                    '& .MuiLinearProgress-bar': {
                      bgcolor: accentBlue,
                      borderRadius: 4
                    }
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
                  {i18n.language === 'es' ? 'Subiendo archivos...' : 'Uploading files...'} {uploadProgress}%
                </Typography>
              </Grid>
            )}

            {!formData.id && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
                  {t('workOrders.priorityLabel')}
                </Typography>
                <ToggleButtonGroup
                  value={formData.priority || 'medium'}
                  exclusive
                  onChange={(e, val) => val && setFormData({ ...formData, priority: val })}
                  sx={{ flexWrap: 'wrap', gap: 1 }}
                >
                  <ToggleButton value="low" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#4caf50', color: 'white', borderColor: '#4caf50 !important' } }}>
                    {t('workOrders.priorityLow')}
                  </ToggleButton>
                  <ToggleButton value="medium" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#2196f3', color: 'white', borderColor: '#2196f3 !important' } }}>
                    {t('workOrders.priorityMedium')}
                  </ToggleButton>
                  <ToggleButton value="high" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#ff9800', color: 'white', borderColor: '#ff9800 !important' } }}>
                    {t('workOrders.priorityHigh')}
                  </ToggleButton>
                  <ToggleButton value="emergency" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#f44336', color: 'white', borderColor: '#f44336 !important' } }}>
                    {t('workOrders.priorityEmergency')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
            )}
            {formData.id && hasRole('administrator', 'supervisor') && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
                    {t('workOrders.priorityLabel')}
                  </Typography>
                  <ToggleButtonGroup
                    value={formData.priority || 'medium'}
                    exclusive
                    onChange={(e, val) => val && setFormData({ ...formData, priority: val })}
                    sx={{ flexWrap: 'wrap', gap: 1 }}
                  >
                    <ToggleButton value="low" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#4caf50', color: 'white', borderColor: '#4caf50 !important' } }}>
                      {t('workOrders.priorityLow')}
                    </ToggleButton>
                    <ToggleButton value="medium" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#2196f3', color: 'white', borderColor: '#2196f3 !important' } }}>
                      {t('workOrders.priorityMedium')}
                    </ToggleButton>
                    <ToggleButton value="high" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#ff9800', color: 'white', borderColor: '#ff9800 !important' } }}>
                      {t('workOrders.priorityHigh')}
                    </ToggleButton>
                    <ToggleButton value="emergency" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#f44336', color: 'white', borderColor: '#f44336 !important' } }}>
                      {t('workOrders.priorityEmergency')}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
                {showAssignmentOptions && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
                      {t('workOrders.issueType') || 'Issue Type'}
                    </Typography>
                    <ToggleButtonGroup
                      value={formData.assignment_type || 'technician'}
                      exclusive
                      onChange={(e, val) => val && setFormData({ ...formData, assignment_type: val, assigned_to: '' })}
                      sx={{ gap: 1 }}
                    >
                      <ToggleButton value="technician" sx={toggleButtonStyle}>
                        {t('workOrders.maintenanceIssue') || 'Maintenance Issue'}
                      </ToggleButton>
                      {isFacilitiesAsset && (
                        <ToggleButton value="janitor" sx={{
                          ...toggleButtonStyle,
                          border: '1.5px solid #4caf50 !important',
                          '&.Mui-selected': { backgroundColor: '#4caf50', color: 'white', '&:hover': { backgroundColor: '#66bb6a' } },
                          '&:hover': { backgroundColor: '#e8f5e9' }
                        }}>
                          {t('workOrders.janitorialRequest') || 'Janitorial Request'}
                        </ToggleButton>
                      )}
                      {isEquipmentAsset && (
                        <ToggleButton value="it_technician" sx={{
                          ...toggleButtonStyle,
                          border: '1.5px solid #9c27b0 !important',
                          '&.Mui-selected': { backgroundColor: '#9c27b0', color: 'white', '&:hover': { backgroundColor: '#ab47bc' } },
                          '&:hover': { backgroundColor: '#f3e5f5' }
                        }}>
                          {t('workOrders.itIssue') || 'IT/Computer Issue'}
                        </ToggleButton>
                      )}
                    </ToggleButtonGroup>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    label={t('workOrders.assignTo')}
                    value={formData.assigned_to || ''}
                    onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  >
                    <MenuItem value="">
                      <em>{t('workOrders.unassigned')}</em>
                    </MenuItem>
                    {getAssignableUsers().map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.full_name} ({user.username})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: accentBlue, fontWeight: 600 }}>
                    {t('workOrders.statusLabel')}
                  </Typography>
                  <ToggleButtonGroup
                    value={formData.status || 'assigned'}
                    exclusive
                    onChange={(e, val) => val && setFormData({ ...formData, status: val })}
                    sx={{ flexWrap: 'wrap', gap: 1 }}
                  >
                    <ToggleButton value="assigned" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#ff9800', color: 'white', borderColor: '#ff9800 !important' } }}>
                      {t('workOrders.statusAssigned')}
                    </ToggleButton>
                    <ToggleButton value="in_progress" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#2196f3', color: 'white', borderColor: '#2196f3 !important' } }}>
                      {t('workOrders.statusInProgress')}
                    </ToggleButton>
                    <ToggleButton value="completed" sx={{ ...toggleButtonStyle, '&.Mui-selected': { backgroundColor: '#4caf50', color: 'white', borderColor: '#4caf50 !important' } }}>
                      {t('workOrders.statusCompleted')}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Grid>
              </>
            )}
            {/* Desktop buttons inside grid */}
            {!isMobile && (
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Button
                  onClick={handleCloseDialog}
                  disabled={submitting}
                  size="medium"
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3,
                    py: 1,
                    fontSize: '0.875rem',
                  }}
                >
                  {t('workOrders.cancel')}
                </Button>
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  size="medium"
                  disabled={submitting}
                  sx={{
                    background: gradientPrimary,
                    borderRadius: 2,
                    textTransform: 'none',
                    px: 3,
                    py: 1,
                    fontSize: '0.875rem',
                  }}
                >
                  {submitting ? (i18n.language === 'es' ? 'Enviando...' : 'Submitting...') : (formData.id ? t('workOrders.update') : t('workOrders.submit'))}
                </Button>
              </Grid>
            )}
            {/* Spacer for mobile sticky buttons */}
            {isMobile && <Grid item xs={12} sx={{ height: 80 }} />}
          </Grid>
        </DialogContent>
        {/* Mobile sticky buttons */}
        {isMobile && (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: 'background.paper',
              borderTop: `1px solid ${borderColor}`,
              p: 2,
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              zIndex: 1,
            }}
          >
            <Button
              onClick={handleCloseDialog}
              disabled={submitting}
              size="large"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                flex: 1,
                maxWidth: 150,
              }}
            >
              {t('workOrders.cancel')}
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              size="large"
              disabled={submitting}
              sx={{
                background: gradientPrimary,
                borderRadius: 2,
                textTransform: 'none',
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                flex: 1,
                maxWidth: 200,
              }}
            >
              {submitting ? (i18n.language === 'es' ? 'Enviando...' : 'Submitting...') : (formData.id ? t('workOrders.update') : t('workOrders.submit'))}
            </Button>
          </Box>
        )}
      </Dialog>
    </Box>
  );
}
